/**
 * Per-event availabilities matrix.
 * Extracted from EventDetail.jsx (Phase 11 refactor — Option 4).
 *
 * Features:
 *  - Event-level slot editor (AM/PM/SOIRÉE)
 *  - Per-date slot overrides (event.availability_slots_per_date)
 *  - Matrix split by role (Arbitres vs Marqueurs) — strict role filtering
 *  - Per-row "Masquer" toggle (event.hidden_official_ids)
 *  - "J'ai assez (envoyer SMS)" button → NotifyCompleteModal
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SlotsEditor from "@/components/SlotsEditor";
import NotifyCompleteModal from "@/components/NotifyCompleteModal";
import { DEFAULT_SLOTS, SLOT_KEYS, SLOT_LABELS } from "@/lib/slots";

function Stat({ label, value, accent }) {
  return (
    <div className={`border ${accent ? "border-black bg-black text-white" : "border-black bg-white"} p-5`}>
      <div className={`label ${accent ? "text-white/60" : ""}`}>{label}</div>
      <div className="font-serif text-5xl mt-1 tabular leading-none">{value}</div>
    </div>
  );
}

export default function AvailabilitiesTab({ eventId, event, officials, onEventChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slotsOverride, setSlotsOverride] = useState(event?.availability_slots || null);
  const [savingSlots, setSavingSlots] = useState(false);
  const [perDateOpen, setPerDateOpen] = useState(false);
  // Hidden officials (event-scoped, par rôle) — persisté sur event.hidden_official_ids_by_role.
  // Permet de masquer un même officiel comme arbitre OU comme marqueur, indépendamment.
  const computeHidden = () => {
    const byRole = event?.hidden_official_ids_by_role;
    if (byRole && typeof byRole === "object") {
      return {
        referee: new Set(byRole.referee || []),
        scorer: new Set(byRole.scorer || []),
      };
    }
    const flat = event?.hidden_official_ids || [];
    return { referee: new Set(flat), scorer: new Set(flat) };
  };
  const [hiddenByRole, setHiddenByRole] = useState(computeHidden);
  const [showHidden, setShowHidden] = useState(false);
  // Notify-complete modal state
  const [notifyOpen, setNotifyOpen] = useState(false);

  useEffect(() => {
    setHiddenByRole(computeHidden());
  }, [event?.hidden_official_ids_by_role, event?.hidden_official_ids]);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.get(`/events/${eventId}/availabilities`);
        setData(res);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  const refreshData = async () => {
    const { data: res } = await api.get(`/events/${eventId}/availabilities`);
    setData(res);
  };

  const saveEventSlots = async (slots) => {
    setSavingSlots(true);
    try {
      const { data: r } = await api.patch(`/events/${eventId}`, { availability_slots: slots });
      setSlotsOverride(slots);
      onEventChange && onEventChange(r.event);
      await refreshData();
      toast.success("Créneaux mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSlots(false);
    }
  };

  const clearEventSlots = async () => {
    setSavingSlots(true);
    try {
      const { data: r } = await api.patch(`/events/${eventId}`, { availability_slots: {} });
      setSlotsOverride(null);
      onEventChange && onEventChange(r.event);
      await refreshData();
      toast.success("Créneaux remis aux défauts");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSlots(false);
    }
  };

  const saveDateSlots = async (date, slots) => {
    try {
      const { data: r } = await api.patch(`/events/${eventId}`, {
        availability_slots_per_date: { [date]: slots },
      });
      onEventChange && onEventChange(r.event);
      await refreshData();
      toast.success(`Créneaux du ${date} mis à jour`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const clearDateSlots = async (date) => {
    try {
      const { data: r } = await api.patch(`/events/${eventId}`, {
        availability_slots_per_date: { [date]: null },
      });
      onEventChange && onEventChange(r.event);
      await refreshData();
      toast.success(`Override du ${date} retiré`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Admin override — reconstruit puis persiste les dispos d'un arbitre après
  // un clic sur une cellule (cycle — → ✓ → ✗) ou sur l'hébergement.
  const persistAdminOverride = async (offId, nextAnswers, nextLodging, nextComment) => {
    try {
      const body = {
        answers: nextAnswers,
        comment: nextComment ?? "",
        needs_lodging: !!nextLodging,
      };
      const { data: r } = await api.put(
        `/events/${eventId}/officials/${offId}/availability`,
        body,
      );
      // Update local state instead of full refresh — plus fluide.
      setData((prev) => {
        if (!prev) return prev;
        const others = prev.answers.filter(
          (a) => !(a.official_id === offId && a.id === r.id),
        );
        return {
          ...prev,
          answers: [
            ...others.filter((a) => a.official_id !== offId),
            ...others.filter((a) => a.official_id === offId),
            {
              id: r.id,
              official_id: offId,
              event_id: eventId,
              answers: r.answers,
              needs_lodging: r.needs_lodging,
              comment: nextComment ?? "",
              updated_at: new Date().toISOString(),
              admin_override_at: new Date().toISOString(),
            },
          ],
        };
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      await refreshData();
    }
  };

  const toggleHidden = async (officialId, role) => {
    const next = {
      referee: new Set(hiddenByRole.referee),
      scorer: new Set(hiddenByRole.scorer),
    };
    if (next[role].has(officialId)) next[role].delete(officialId);
    else next[role].add(officialId);
    setHiddenByRole(next);
    try {
      const { data: r } = await api.put(`/events/${eventId}/hidden-officials`, {
        by_role: {
          referee: [...next.referee],
          scorer: [...next.scorer],
        },
      });
      onEventChange && onEventChange({
        ...event,
        hidden_official_ids: r.hidden_official_ids,
        hidden_official_ids_by_role: r.hidden_official_ids_by_role,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setHiddenByRole(hiddenByRole); // rollback
    }
  };

  if (loading) return <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse">Chargement…</p>;

  const slots = data?.slots || DEFAULT_SLOTS;
  const hasOverride = !!slotsOverride;

  // Known dates: from games and from availability requests
  const knownDates = (() => {
    const set = new Set();
    (event?.games || []).forEach((g) => { if (g.date) set.add(g.date); });
    (data?.requests || []).forEach((r) => (r.dates || []).forEach((d) => set.add(d)));
    if (event?.date) set.add(event.date);
    return [...set].sort();
  })();
  const perDateOverrides = event?.availability_slots_per_date || {};
  const slotsPerDate = data?.slots_per_date || {};

  const slotEditorBlock = (
    <div className="card-flat mb-6" data-testid="event-slots-editor">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="label !mb-0">Créneaux AM / PM / SOIRÉE pour cet événement</div>
          {hasOverride ? (
            <span className="badge-dark !text-[9px]">Override actif</span>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Défauts globaux</span>
          )}
        </div>
        {hasOverride && (
          <button
            onClick={clearEventSlots}
            disabled={savingSlots}
            className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black"
            data-testid="reset-event-slots"
          >
            Utiliser les défauts
          </button>
        )}
      </div>
      <SlotsEditor
        slots={slots}
        onSave={saveEventSlots}
        testidPrefix="event-slot"
        showReset={false}
      />

      {knownDates.length > 0 && (
        <div className="mt-6 border-t border-[var(--border-strong)] pt-4">
          <button
            onClick={() => setPerDateOpen(!perDateOpen)}
            className="flex items-center justify-between w-full text-left"
            data-testid="toggle-per-date-overrides"
          >
            <div className="flex items-center gap-2">
              <span className="label !mb-0">Personnaliser les heures par date</span>
              {Object.keys(perDateOverrides).length > 0 && (
                <span className="badge-dark !text-[9px]">{Object.keys(perDateOverrides).length} override(s)</span>
              )}
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
              {perDateOpen ? "Réduire ▲" : "Déplier ▼"}
            </span>
          </button>
          {perDateOpen && (
            <div className="mt-4 space-y-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                Ex: AM 09:00-13:00 le dimanche mais AM 08:00-12:00 le samedi.
              </p>
              {knownDates.map((d) => {
                const overridden = !!perDateOverrides[d];
                const dSlots = slotsPerDate[d] || slots;
                return (
                  <div key={d} className="border border-[var(--border-strong)] p-3" data-testid={`per-date-row-${d}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="label !mb-0">{d}</div>
                      <div className="flex items-center gap-2">
                        {overridden ? (
                          <span className="badge-dark !text-[9px]">Personnalisé</span>
                        ) : (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Hérité</span>
                        )}
                        {overridden && (
                          <button
                            onClick={() => clearDateSlots(d)}
                            className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black"
                            data-testid={`clear-date-slots-${d}`}
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                    </div>
                    <SlotsEditor
                      slots={dSlots}
                      onSave={(s) => saveDateSlots(d, s)}
                      testidPrefix={`date-slot-${d}`}
                      showReset={false}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (!data || data.requests.length === 0) {
    return (
      <div data-testid="availabilities-tab">
        {slotEditorBlock}
        <div className="border-2 border-dashed border-[var(--border-strong)] p-12 text-center" data-testid="no-availability-requests">
          <p className="font-serif text-3xl italic mb-2">Aucune demande envoyée</p>
          <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] mb-4">
            Va dans &quot;Officiels&quot; pour envoyer une demande SMS à ton personnel.
          </p>
        </div>
      </div>
    );
  }

  // Aggregate
  const allDates = [...new Set(data.requests.flatMap((r) => r.dates))].sort();
  const allOfficialIds = [...new Set(data.requests.flatMap((r) => r.official_ids))]
    .map((id) => ({ id, o: officials.find((x) => x.id === id) }))
    .sort((a, b) => {
      const fa = (a.o?.first_name || "").trim();
      const fb = (b.o?.first_name || "").trim();
      const cmp = fa.localeCompare(fb, "fr", { sensitivity: "base" });
      if (cmp !== 0) return cmp;
      return (a.o?.last_name || "").localeCompare(b.o?.last_name || "", "fr", { sensitivity: "base" });
    })
    .map((x) => x.id);
  const ansByOff = {};
  data.answers.forEach((a) => {
    if (!ansByOff[a.official_id]) ansByOff[a.official_id] = {};
    Object.entries(a.answers).forEach(([d, v]) => {
      ansByOff[a.official_id][d] = v;
    });
  });

  const slotAnswer = (offId, date, slot) => {
    const raw = ansByOff[offId]?.[date];
    if (raw == null) return null;
    if (typeof raw === "string") return raw;
    return raw[slot] || null;
  };

  const summary = (() => {
    let yes = 0;
    data.answers.forEach((a) => {
      Object.values(a.answers).forEach((v) => {
        if (v === "yes") yes += 3;
        else if (v && typeof v === "object") {
          SLOT_KEYS.forEach((sk) => { if (v[sk] === "yes") yes += 1; });
        }
      });
    });
    // Compter les UNIQUES official_id qui ont répondu (un officiel peut avoir
    // plusieurs answers en base pour un même event si plusieurs requests envoyées).
    const uniqueResponders = new Set(data.answers.map((a) => a.official_id)).size;
    return { yes, responded: uniqueResponders, total: allOfficialIds.length };
  })();

  const sortByFirstName = (a, b) => {
    const oa = officials.find((x) => x.id === a);
    const ob = officials.find((x) => x.id === b);
    const fa = (oa?.first_name || "").trim();
    const fb = (ob?.first_name || "").trim();
    const cmp = fa.localeCompare(fb, "fr", { sensitivity: "base" });
    if (cmp !== 0) return cmp;
    return (oa?.last_name || "").localeCompare(ob?.last_name || "", "fr", { sensitivity: "base" });
  };
  const refIds = allOfficialIds
    .filter((id) => {
      const o = officials.find((x) => x.id === id);
      return o && Array.isArray(o.roles) && o.roles.includes("referee");
    })
    .sort(sortByFirstName);
  const scIds = allOfficialIds
    .filter((id) => {
      const o = officials.find((x) => x.id === id);
      return o && Array.isArray(o.roles) && o.roles.includes("scorer");
    })
    .sort(sortByFirstName);

  // Visibility per-role
  const hiddenCount = new Set([
    ...[...hiddenByRole.referee].filter((id) => allOfficialIds.includes(id)),
    ...[...hiddenByRole.scorer].filter((id) => allOfficialIds.includes(id)),
  ]).size;
  const isVisibleForRole = (id, role) => showHidden || !hiddenByRole[role]?.has(id);

  const playableGameAssignees = new Set();
  (event?.games || []).forEach((g) => {
    if (g.is_break) return;
    if (g.referee1_id) playableGameAssignees.add(g.referee1_id);
    if (g.referee2_id) playableGameAssignees.add(g.referee2_id);
    if (g.scorer_id) playableGameAssignees.add(g.scorer_id);
  });
  const unassignedRespondersIds = allOfficialIds.filter(
    (id) => data.answers.some((a) => a.official_id === id) && !playableGameAssignees.has(id)
  );

  const renderMatrix = (sectionTitle, ids, sectionTestid, role) => {
    if (ids.length === 0) return null;
    const visibleIds = ids.filter((id) => isVisibleForRole(id, role));
    return (
      <div className="mb-8" data-testid={sectionTestid}>
        <h3 className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] mb-2">
          {sectionTitle} <span className="text-[var(--muted-2)] normal-case">({visibleIds.length}{ids.length !== visibleIds.length ? ` / ${ids.length}` : ""})</span>
        </h3>
        <div className="border-t-2 border-b-2 border-black bg-white overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b-2 border-black">
                <th rowSpan={2} className="py-3 px-3 text-left label sticky left-0 bg-white z-10 align-middle">Officiel</th>
                {allDates.map((d) => {
                  const overridden = !!perDateOverrides[d];
                  return (
                    <th key={d} colSpan={3} className="py-2 px-2 text-center label whitespace-nowrap border-l border-black">
                      {d}
                      {overridden && <span className="ml-1 font-mono text-[9px] normal-case tracking-normal text-[var(--muted)]">(perso)</span>}
                    </th>
                  );
                })}
                <th rowSpan={2} className="py-3 px-3 text-center label align-middle border-l border-black" title="Hébergement demandé">Héberg.</th>
                <th rowSpan={2} className="py-3 px-3 text-left label align-middle border-l border-black">Commentaire</th>
                <th rowSpan={2} className="py-3 px-3 text-right label align-middle border-l border-black">Action</th>
              </tr>
              <tr className="border-b-2 border-black">
                {allDates.flatMap((d) => {
                  const dSlots = slotsPerDate[d] || slots;
                  return SLOT_KEYS.map((sk, i) => (
                    <th
                      key={`${d}-${sk}`}
                      className={`py-1.5 px-1 text-center font-mono text-[9px] uppercase tracking-wider ${i === 0 ? "border-l border-black" : "border-l border-[var(--border)]"}`}
                      title={`${dSlots[sk].start}–${dSlots[sk].end}`}
                    >
                      {SLOT_LABELS[sk]}
                      <div className="font-mono text-[8px] text-[var(--muted-2)] normal-case tracking-normal">
                        {dSlots[sk].start}–{dSlots[sk].end}
                      </div>
                    </th>
                  ));
                })}
              </tr>
            </thead>
            <tbody>
              {visibleIds.map((offId) => {
                const o = officials.find((x) => x.id === offId);
                if (!o) return null;
                // Sélectionne la RÉPONSE LA PLUS RÉCENTE en cas de doublons
                // (un officiel peut avoir plusieurs requests pour un même event).
                const officialAnswers = data.answers.filter((a) => a.official_id === offId);
                const ans = officialAnswers.sort((a, b) =>
                  (b.updated_at || "").localeCompare(a.updated_at || "")
                )[0];
                const isHidden = hiddenByRole[role]?.has(offId);
                return (
                  <tr
                    key={offId}
                    className={`border-b border-[var(--border)] hover:bg-[var(--surface-3)] ${isHidden ? "opacity-50" : ""}`}
                    data-testid={`avail-row-${offId}`}
                  >
                    <td className="py-2 px-3 font-bold sticky left-0 bg-white">{o.first_name} {o.last_name}</td>
                    {allDates.flatMap((d) =>
                      SLOT_KEYS.map((sk, i) => {
                        const v = slotAnswer(offId, d, sk);
                        const cycleNext = { yes: "no", no: null, null: "yes" };
                        const handleCycle = () => {
                          const cur = v || "null";
                          const nxt = cycleNext[cur];
                          // Reconstruit la map complète des dispos pour cet arbitre
                          // à partir de l'état local, puis met à jour ce slot.
                          const currentPerDate = { ...(ansByOff[offId] || {}) };
                          const dateVal = currentPerDate[d];
                          let dateObj;
                          if (dateVal == null) {
                            dateObj = {};
                          } else if (typeof dateVal === "string") {
                            // Convertit format legacy en slot-object avant modif.
                            dateObj = { am: dateVal, pm: dateVal, soiree: dateVal };
                          } else {
                            dateObj = { ...dateVal };
                          }
                          if (nxt) dateObj[sk] = nxt;
                          else delete dateObj[sk];
                          const nextAnswers = { ...currentPerDate, [d]: dateObj };
                          persistAdminOverride(offId, nextAnswers, ans?.needs_lodging, ans?.comment);
                        };
                        return (
                          <td
                            key={`${d}-${sk}`}
                            className={`py-2 px-1 text-center cursor-pointer hover:bg-black/5 select-none ${i === 0 ? "border-l border-black" : "border-l border-[var(--border)]"}`}
                            onClick={handleCycle}
                            title="Clic : ✓ → ✗ → — (modification admin)"
                            data-testid={`avail-cell-${role}-${offId}-${d}-${sk}`}
                          >
                            {v === "yes" && <span className="badge-dark !px-1.5">✓</span>}
                            {v === "no" && <span className="badge border-2 border-double-thick !px-1.5">✗</span>}
                            {!v && <span className="text-[var(--muted-2)] font-mono text-[10px]">—</span>}
                          </td>
                        );
                      })
                    )}
                    <td
                      className="py-2 px-3 text-center border-l border-black cursor-pointer hover:bg-black/5 select-none"
                      onClick={() => {
                        const nextLodging = !ans?.needs_lodging;
                        persistAdminOverride(offId, ansByOff[offId] || {}, nextLodging, ans?.comment);
                      }}
                      title="Clic pour basculer hébergement (modification admin)"
                      data-testid={`lodging-${role}-${offId}`}
                    >
                      {ans?.needs_lodging ? (
                        <span className="badge-dark !px-1.5" title="Hébergement demandé">Oui</span>
                      ) : (
                        <span className="text-[var(--muted-2)] font-mono text-[10px]">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-[var(--muted)] italic max-w-xs truncate border-l border-black">{ans?.comment || ""}</td>
                    <td className="py-2 px-3 text-right border-l border-black whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => toggleHidden(offId, role)}
                        className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black"
                        data-testid={`toggle-hide-${role}-${offId}`}
                        title={isHidden ? "Réafficher" : `Masquer comme ${role === "referee" ? "arbitre" : "marqueur"} (j'ai assez de monde)`}
                      >
                        {isHidden ? "Réafficher" : "Masquer"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="availabilities-tab">
      {slotEditorBlock}

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label="Officiels contactés" value={summary.total} />
        <Stat label="Ont répondu" value={`${summary.responded}/${summary.total}`} />
        <Stat label="Créneaux dispo" value={summary.yes} accent />
      </div>

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
          {(() => {
            const overrideCount = Object.keys(perDateOverrides).length;
            if (overrideCount === 0) {
              return (
                <>AM ({slots.am.start}–{slots.am.end}) · PM ({slots.pm.start}–{slots.pm.end}) · SOIRÉE ({slots.soiree.start}–{slots.soiree.end})
                  {data.requests.length > 1 && <> · {data.requests.length} demandes envoyées</>}</>
              );
            }
            return (
              <>Créneaux personnalisés sur {overrideCount} date(s)
                {data.requests.length > 1 && <> · {data.requests.length} demandes envoyées</>}</>
            );
          })()}
        </p>
        <div className="flex items-center gap-2">
          {hiddenCount > 0 && (
            <button
              onClick={() => setShowHidden(!showHidden)}
              className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black border border-[var(--border-strong)] px-2 py-1"
              data-testid="toggle-show-hidden"
            >
              {showHidden ? `Cacher les ${hiddenCount} masqué(s)` : `Voir les ${hiddenCount} masqué(s)`}
            </button>
          )}
          <button
            onClick={() => setNotifyOpen(true)}
            disabled={unassignedRespondersIds.length === 0}
            className="font-mono text-[10px] uppercase tracking-widest border-2 border-black bg-white text-black hover:bg-black hover:text-white px-3 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="notify-complete-btn"
            title={unassignedRespondersIds.length === 0 ? "Tous les répondants sont déjà assignés" : `Envoyer un SMS à ${unassignedRespondersIds.length} répondant(s) non assigné(s)`}
          >
            ☑ J&apos;ai assez (envoyer SMS)
          </button>
        </div>
      </div>

      {renderMatrix("Arbitres", refIds, "matrix-referees", "referee")}
      {renderMatrix("Marqueurs", scIds, "matrix-scorers", "scorer")}

      {notifyOpen && (
        <NotifyCompleteModal
          eventId={eventId}
          eventName={event?.name || ""}
          candidates={unassignedRespondersIds.map((id) => officials.find((o) => o.id === id)).filter(Boolean)}
          onClose={() => setNotifyOpen(false)}
        />
      )}
    </div>
  );
}
